import * as React from 'react'
import { ControllerProps, FieldPath, FieldValues } from 'react-hook-form'

import Checkbox, {
  ICheckboxProps
} from '@/components-latest/primitives/Controls/Checkbox'
import DatePicker, {
  IDatepickerProps
} from '@/components-latest/primitives/Controls/DatePicker/DatePicker'
import Input, {
  IInputProps
} from '@/components-latest/primitives/Controls/Input'
import NumericInput, {
  INumericInputProps
} from '@/components-latest/primitives/Controls/NumericInput'
import Label from '@/components-latest/primitives/Label'
import Text from '@/components-latest/primitives/Text'
import Toggle, { IToggleProps } from '@/components-latest/primitives/Toggle'
import { FormControl, FormField } from '@/components-latest/shared/Form/Form'
import AvatarInput, { IAvatarInputProps } from '@/components-latest/primitives/Controls/AvatarInput/AvatarInput';

const DatePickerField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  ...restProps
}: Partial<IDatepickerProps> &
  Pick<ControllerProps<TFieldValues, TName>, 'name' | 'control'>) => {
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl>
          <DatePicker {...field} {...restProps} />
        </FormControl>
      )}
    />
  )
}

const NumericField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  ...restProps
}: Pick<ControllerProps<TFieldValues, TName>, 'control' | 'name'> &
  Partial<INumericInputProps>) => {
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl>
          <NumericInput {...field} {...restProps} />
        </FormControl>
      )}
    />
  )
}

const InputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  ...restProps
}: Pick<ControllerProps<TFieldValues, TName>, 'control' | 'name'> &
  IInputProps) => {
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl>
          <Input {...field} {...restProps} />
        </FormControl>
      )}
    />
  )
}

const AvatarInputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
  >({
      name,
      placeholderString,
      className,
      control,
      ...restProps
    }: Pick<ControllerProps<TFieldValues, TName>, 'control' | 'name'> &
  IAvatarInputProps) => {
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl>
          <AvatarInput
            placeholderString={placeholderString}
            {...field}
            {...restProps}
          />
        </FormControl>
      )}
    />
  )
}

const GroupCheckboxField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  label = '',
  value: fieldValue,
  ...restProps
}: Pick<ControllerProps<TFieldValues, TName>, 'control' | 'name'> &
  Partial<ICheckboxProps> & { label?: string }) => (
  <FormField
    name={name}
    control={control}
    render={({ field: { value: groupValue, ...field } }) => {
      return (
        <FormControl>
          <Label layout="spaced-row">
            <Text variant="nextSubtitle">{label}</Text>
            <Checkbox
              {...field}
              value={fieldValue}
              name={field.name}
              checked={groupValue.includes(fieldValue)}
              onCheckedChange={checked => {
                const valuesSet = new Set(groupValue)
                checked
                  ? valuesSet.add(fieldValue)
                  : valuesSet.delete(fieldValue)

                field.onChange([...valuesSet])
              }}
              {...restProps}
            />
          </Label>
        </FormControl>
      )
    }}
  />
)

const GroupToggleField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  children,
  label,
  value: fieldValue,
  ...restProps
}: Pick<ControllerProps<TFieldValues, TName>, 'control' | 'name'> &
  Partial<IToggleProps> & { label: string }) => (
  <FormField
    name={name}
    control={control}
    render={({ field: { value: groupValue, ...field } }) => {
      return (
        <FormControl>
          <Toggle
            {...field}
            value={fieldValue}
            name={field.name}
            pressed={groupValue === fieldValue}
            onPressedChange={() => {
              field.onChange(fieldValue)
            }}
            {...restProps}>
            {label}
          </Toggle>
        </FormControl>
      )
    }}
  />
)

export {
  DatePickerField,
  GroupCheckboxField,
  GroupToggleField,
  InputField,
  NumericField,
  AvatarInputField
}
